import React, { useState, useMemo } from 'react';

const ACTOR_TYPE_LABELS = {
  'state': 'Estat',
  'non-state': 'No Estatal',
  'individual': 'Individual',
  'group': 'Grup',
  'unknown': 'Desconegut'
};

const SORT_OPTIONS = [
  { value: 'threat_desc', label: 'Amenaça (major a menor)' },
  { value: 'threat_asc', label: 'Amenaça (menor a major)' },
  { value: 'name_asc', label: 'Nom (A-Z)' },
  { value: 'name_desc', label: 'Nom (Z-A)' },
  { value: 'date_desc', label: 'Més recent primer' },
  { value: 'date_asc', label: 'Més antic primer' }
];

function ActorPanel({ actors, templates, onAdd, onEdit, onDelete, isReadOnly }) {
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [expandedActor, setExpandedActor] = useState(null);
  const [sortBy, setSortBy] = useState('threat_desc');

  const getThreatColor = (score) => {
    if (score === null || score === undefined) return 'bg-surface-muted';
    if (score >= 8) return 'bg-red-500';
    if (score >= 6) return 'bg-orange-500';
    if (score >= 4) return 'bg-yellow-500';
    if (score >= 2) return 'bg-green-500';
    return 'bg-green-300';
  };

  const getThreatTextColor = (score) => {
    if (score === null || score === undefined) return 'text-content-secondary';
    if (score >= 8) return 'text-error';
    if (score >= 6) return 'text-warning';
    if (score >= 4) return 'text-warning';
    return 'text-success';
  };

  const getScoreBarColor = (value) => {
    if (!value) return 'bg-surface-muted';
    if (value >= 4) return 'bg-red-400';
    if (value >= 3) return 'bg-orange-400';
    if (value >= 2) return 'bg-yellow-400';
    return 'bg-green-400';
  };

  const isComplete = (actor) => {
    return actor.intentionScore && actor.capabilityScore && actor.opportunityScore &&
           actor.intentionAnalysis && actor.capabilityAnalysis && actor.opportunityAnalysis;
  };

  // Sort actors based on selected option
  const sortedActors = useMemo(() => {
    const sorted = [...actors];
    switch (sortBy) {
      case 'threat_desc':
        return sorted.sort((a, b) => (b.threatScore || 0) - (a.threatScore || 0));
      case 'threat_asc':
        return sorted.sort((a, b) => (a.threatScore || 0) - (b.threatScore || 0));
      case 'name_asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name_desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'date_desc':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'date_asc':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      default:
        return sorted;
    }
  }, [actors, sortBy]);

  const ScoreIndicator = ({ label, score, analysis }) => (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-content-muted w-16">{label}</span>
      <div className="flex items-center flex-1 ml-2">
        <div className="flex space-x-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-sm ${i <= (score || 0) ? getScoreBarColor(score) : 'bg-surface-muted'}`}
            />
          ))}
        </div>
        <span className="ml-2 text-xs font-medium text-content-secondary w-4">{score || '-'}</span>
        {!analysis && (
          <svg className="w-3 h-3 ml-1 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-surface">
      {/* Header */}
      <div className="p-4 border-b border-border bg-surface">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-content flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Actors ({actors.length})
          </h3>
          {!isReadOnly && (
            <button
              onClick={() => onAdd()}
              className="px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent-hover flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Afegir
            </button>
          )}
        </div>

        {/* Sort selector */}
        {actors.length > 1 && (
          <div className="flex items-center">
            <label className="text-xs text-content-muted mr-2">Ordenar:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs border border-border-strong rounded px-2 py-1 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Actor Cards */}
      <div className="flex-1 overflow-y-auto p-4">
        {actors.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 mx-auto text-content-disabled mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-content-muted text-sm">No hi ha actors definits</p>
            {!isReadOnly && (
              <button
                onClick={() => onAdd()}
                className="mt-3 text-accent hover:text-accent-hover text-sm font-medium"
              >
                Afegir el primer actor
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-3">
            {sortedActors.map((actor) => (
              <div
                key={actor.id}
                className={`bg-surface rounded-lg border ${isComplete(actor) ? 'border-border' : 'border-warning/30'} hover:border-border-strong transition-colors`}
              >
                {/* Card Header with Threat Score */}
                <div className="p-3">
                  <div className="flex items-start">
                    {/* Threat Score Circle */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-full ${getThreatColor(actor.threatScore)} flex flex-col items-center justify-center mr-3`}>
                      <span className="text-white font-bold text-lg leading-none">
                        {actor.threatScore !== null ? actor.threatScore.toFixed(1) : '-'}
                      </span>
                      <span className="text-white text-[8px] uppercase tracking-wide">amenaça</span>
                    </div>

                    {/* Actor Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-content truncate">{actor.name}</h4>
                        {actor.actorType && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-surface-muted text-content-secondary rounded flex-shrink-0">
                            {ACTOR_TYPE_LABELS[actor.actorType] || actor.actorType}
                          </span>
                        )}
                      </div>

                      {/* I-C-O Scores */}
                      <div className="mt-2">
                        <ScoreIndicator label="I" score={actor.intentionScore} analysis={actor.intentionAnalysis} />
                        <ScoreIndicator label="C" score={actor.capabilityScore} analysis={actor.capabilityAnalysis} />
                        <ScoreIndicator label="O" score={actor.opportunityScore} analysis={actor.opportunityAnalysis} />
                      </div>
                    </div>
                  </div>

                  {/* Incomplete warning */}
                  {!isComplete(actor) && (
                    <div className="mt-2 px-2 py-1 bg-warning-subtle rounded text-xs text-warning-text flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Incomplet
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between">
                    <button
                      onClick={() => setExpandedActor(actor)}
                      className="text-xs text-accent hover:text-accent-hover font-medium flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Veure detalls
                    </button>
                    {!isReadOnly && (
                      <div className="flex space-x-1">
                        <button
                          onClick={() => onEdit(actor)}
                          className="p-1.5 text-content-muted hover:text-content-secondary hover:bg-surface-muted rounded"
                          title="Editar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(actor)}
                          className="p-1.5 text-error hover:text-error-text hover:bg-error-subtle rounded"
                          title="Eliminar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actor Detail Modal */}
      {expandedActor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setExpandedActor(null)}>
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-border flex items-center justify-between bg-surface-subtle">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-full ${getThreatColor(expandedActor.threatScore)} flex items-center justify-center mr-3`}>
                  <span className="text-white font-bold text-lg">
                    {expandedActor.threatScore !== null ? expandedActor.threatScore.toFixed(1) : '-'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-content">{expandedActor.name}</h3>
                  {expandedActor.actorType && (
                    <span className="text-sm text-content-muted">
                      {ACTOR_TYPE_LABELS[expandedActor.actorType] || expandedActor.actorType}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setExpandedActor(null)}
                className="p-2 text-content-disabled hover:text-content-secondary rounded-full hover:bg-surface-muted"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Description */}
              {expandedActor.description && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-content-secondary mb-1">Descripció</h4>
                  <p className="text-sm text-content-secondary bg-surface-subtle rounded p-3">{expandedActor.description}</p>
                </div>
              )}

              {/* I-C-O Analysis Grid */}
              <div className="space-y-4">
                {/* Intention */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-surface-subtle px-4 py-2 flex items-center justify-between">
                    <h4 className="font-medium text-content">Intenció (I)</h4>
                    <div className="flex items-center">
                      <div className="flex space-x-0.5 mr-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`w-5 h-5 rounded ${i <= (expandedActor.intentionScore || 0) ? getScoreBarColor(expandedActor.intentionScore) : 'bg-surface-muted'}`}
                          />
                        ))}
                      </div>
                      <span className={`font-bold ${getThreatTextColor(expandedActor.intentionScore)}`}>
                        {expandedActor.intentionScore || '-'}/5
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-surface">
                    {expandedActor.intentionAnalysis ? (
                      <p className="text-sm text-content-secondary whitespace-pre-wrap">{expandedActor.intentionAnalysis}</p>
                    ) : (
                      <p className="text-sm text-content-disabled italic">Sense anàlisi</p>
                    )}
                  </div>
                </div>

                {/* Capability */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-surface-subtle px-4 py-2 flex items-center justify-between">
                    <h4 className="font-medium text-content">Capacitat (C)</h4>
                    <div className="flex items-center">
                      <div className="flex space-x-0.5 mr-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`w-5 h-5 rounded ${i <= (expandedActor.capabilityScore || 0) ? getScoreBarColor(expandedActor.capabilityScore) : 'bg-surface-muted'}`}
                          />
                        ))}
                      </div>
                      <span className={`font-bold ${getThreatTextColor(expandedActor.capabilityScore)}`}>
                        {expandedActor.capabilityScore || '-'}/5
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-surface">
                    {expandedActor.capabilityAnalysis ? (
                      <p className="text-sm text-content-secondary whitespace-pre-wrap">{expandedActor.capabilityAnalysis}</p>
                    ) : (
                      <p className="text-sm text-content-disabled italic">Sense anàlisi</p>
                    )}
                  </div>
                </div>

                {/* Opportunity */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-surface-subtle px-4 py-2 flex items-center justify-between">
                    <h4 className="font-medium text-content">Oportunitat (O)</h4>
                    <div className="flex items-center">
                      <div className="flex space-x-0.5 mr-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`w-5 h-5 rounded ${i <= (expandedActor.opportunityScore || 0) ? getScoreBarColor(expandedActor.opportunityScore) : 'bg-surface-muted'}`}
                          />
                        ))}
                      </div>
                      <span className={`font-bold ${getThreatTextColor(expandedActor.opportunityScore)}`}>
                        {expandedActor.opportunityScore || '-'}/5
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-surface">
                    {expandedActor.opportunityAnalysis ? (
                      <p className="text-sm text-content-secondary whitespace-pre-wrap">{expandedActor.opportunityAnalysis}</p>
                    ) : (
                      <p className="text-sm text-content-disabled italic">Sense anàlisi</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Formula explanation */}
              <div className="mt-4 p-3 bg-accent-subtle rounded-lg">
                <p className="text-xs text-accent-hover">
                  <strong>Fórmula Singer:</strong> Amenaça = (I × C × O) / 12.5
                  {expandedActor.intentionScore && expandedActor.capabilityScore && expandedActor.opportunityScore && (
                    <span className="ml-2">
                      = ({expandedActor.intentionScore} × {expandedActor.capabilityScore} × {expandedActor.opportunityScore}) / 12.5 = {expandedActor.threatScore?.toFixed(2)}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border flex justify-end space-x-3 bg-surface-subtle">
              {!isReadOnly && (
                <button
                  onClick={() => {
                    setExpandedActor(null);
                    onEdit(expandedActor);
                  }}
                  className="px-4 py-2 text-sm bg-accent text-white rounded hover:bg-accent-hover"
                >
                  Editar Actor
                </button>
              )}
              <button
                onClick={() => setExpandedActor(null)}
                className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded hover:bg-surface-subtle"
              >
                Tancar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-content">Eliminar Actor</h3>
            </div>
            <div className="p-4">
              <p className="text-content-secondary">
                Estàs segur que vols eliminar l'actor <strong className="text-content">{showDeleteModal.name}</strong>?
              </p>
              <p className="text-sm text-content-muted mt-2">
                Aquesta acció no es pot desfer.
              </p>
            </div>
            <div className="p-4 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded hover:bg-surface-subtle"
              >
                Cancel·lar
              </button>
              <button
                onClick={() => {
                  onDelete(showDeleteModal.id);
                  setShowDeleteModal(null);
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActorPanel;
